var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ConferenceStreamType } from "../model";
import { TRANSCEIVER_STOPPED_FIELD, uuid } from "../../../Common";
import { joinedConferenceMetadataByKeySelector, streamMetadataSelector, } from "../selectors";
import log from "../../../log";
/**
 * Wrappers around server requests and the data needed
 */
export class ViewStream {
    /**
     * Initialize objects used throughout viewstream's lifecycle
     * @param csi - the context conference
     * @param action - the action that initiated all
     */
    constructor(csi, action) {
        this.csi = csi;
        this.muted = {
            audio: false,
            video: false,
        };
        this.enabled = {
            audio: false,
            video: false,
        };
        this.viewStreamCount = 0;
        this.conferenceId = action.payload.conferenceId;
        this.userId = action.payload.userId;
        this.userEndpointId = action.payload.userEndpointId;
        this.streamId = action.payload.streamId;
        this.streamType = action.payload.streamType;
        this.enabled.audio = [
            ConferenceStreamType.MIC,
            ConferenceStreamType.MIC_AND_CAM,
        ].includes(this.streamType);
        this.enabled.video = [
            ConferenceStreamType.SCREEN,
            ConferenceStreamType.MIC_AND_CAM,
            ConferenceStreamType.CAM,
        ].includes(this.streamType);
        this.streamEvent = {
            id: this.streamId,
            mediaStream: null,
            type: this.streamType === ConferenceStreamType.MIC_AND_CAM
                ? "video"
                : this.streamType,
            originator: {
                username: this.userId,
                endpoint: this.userEndpointId,
            },
            isMuted: this.isMuted.bind(this),
            getMetadata: () => {
                return streamMetadataSelector(this.csi.getParentContext().getStore().getState(), this.conferenceId, this.userId, this.userEndpointId, this.streamId);
            },
        };
        this.body = {
            conferenceId: this.conferenceId,
            userEndpointId: csi.getParentContext().endpoint(),
            streamId: this.streamId,
            sdpOffer: null,
        };
    }
    setRtc(r) {
        this.rtc = r;
    }
    isMuted(kind) {
        // negated conditional
        return !this.enabled[kind] || this.muted[kind];
    }
    /**
     * set track enabled according to mute and hold flags
     * @param context
     */
    updateMediaState(kind) {
        if (!this.rtc) {
            return;
        }
        this.rtc.setTrackKindEnabled(kind, !(this.csi.isOnHold() || this.isMuted(kind)));
    }
    /**
     * update local mediastream to contain enabled tracks of rtc receivers
     */
    syncMedia() {
        let audioTrack = null;
        let videoTrack = null;
        // from rtc get enabled audio and video tracks of live receivers
        if (this.rtc) {
            const tracks = this.rtc.pc
                .getTransceivers()
                .filter((trans) => !trans[TRANSCEIVER_STOPPED_FIELD] &&
                !!trans.receiver &&
                !!trans.receiver.track)
                .map((trans) => trans.receiver.track);
            audioTrack = tracks.find((track) => track.enabled && track.kind === "audio");
            videoTrack = tracks.find((track) => track.enabled && track.kind === "video");
        }
        this.streamEvent.mediaStream = new MediaStream();
        if (audioTrack) {
            this.streamEvent.mediaStream.addTrack(audioTrack);
        }
        if (videoTrack) {
            this.streamEvent.mediaStream.addTrack(videoTrack);
        }
        return this.streamEvent.mediaStream;
    }
    /**
     * syncs local mute flag with that of the store, sends event to client if changed
     * @param kind the track kind to sync
     */
    syncMuteState(kind, notifyClient = true) {
        const metadata = joinedConferenceMetadataByKeySelector(this.csi.getParentContext().getStore().getState(), this.csi.conference.name, "TRACK_MUTED/" + kind + "/" + this.streamId);
        const change = metadata ? JSON.parse(metadata[0].value) : false;
        // if mute state has changed, send event to client with only enabled tracks
        if (this.setMuteState(kind, change)) {
            this.syncMedia();
            if (notifyClient) {
                const eventName = this.isMuted(kind) ? "trackMuted" : "trackUnmuted";
                this.csi.sendEvent(eventName, Object.assign(Object.assign({}, this.streamEvent), { trackKind: kind }));
            }
        }
    }
    /**
     * Changes local mute flag and updates tracks
     * @return if flag was changed
     */
    setMuteState(kind, enabled) {
        if (this.isMuted(kind) === enabled) {
            return false;
        }
        this.muted[kind] = enabled;
        // actually change state
        this.updateMediaState(kind);
        return true;
    }
    /**
     * calls rest api viewStream and then peer connection setRemoteDescription
     */
    *requestAnswer(sdpOffer) {
        this.body.sdpOffer = sdpOffer;
        this.body.viewerId = this.viewStreamCount++ + "-" + uuid();
        if (this.csi.getViewStreamMetadataProvider() !== undefined) {
            this.body.metadata = this.csi.getViewStreamMetadataProvider()(this.streamEvent);
        }
        this.response = yield this.csi
            .getConferenceResource()
            .create(this.body, { urlPostfix: "viewStream" });
        this.answerReceived = true;
    }
    /**
     * calls stopViewStream and ignores any failure
     */
    *stopStream() {
        const _a = this.body, { sdpOffer, metadata } = _a, data = __rest(_a, ["sdpOffer", "metadata"]);
        try {
            if (this.answerReceived) {
                yield this.csi
                    .getConferenceResource()
                    .create(data, { urlPostfix: "stopViewStream" });
                this.answerReceived = false;
            }
        }
        catch (error) {
            log.error("stopViewStream conference request failed with ", error);
        }
    }
    /**
     * calls addViewStreamIceCandidates and ignores any failure
     */
    *addCandidateRequest(iceCandidates) {
        const _a = this.body, { sdpOffer, metadata } = _a, data = __rest(_a, ["sdpOffer", "metadata"]);
        try {
            yield this.csi.getConferenceResource().create(Object.assign(Object.assign({}, data), { iceCandidates }), { urlPostfix: "addViewStreamIceCandidates" });
        }
        catch (error) {
            log.error("addViewStreamIceCandidates conference request failed with ", error);
        }
    }
}
